import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Minus, Plus } from 'lucide-react';
import { type TaskChecklist } from '@apm/contracts';
import { cn } from '../../lib/utils';

/**
 * Đổi tiến độ bằng **một cú bấm hoặc một cú kéo**, không phải ô nhập số.
 *
 * docs/frontend-guide.md §3 xếp "ô nhập 70" vào cột việc-không-nên-làm: mục
 * tiêu của spec mục 13 là cập nhật xong trong vài giây.
 *
 * ## Thanh tiến độ VÀ thanh trượt là MỘT thứ
 *
 * Trước đây thẻ có hai thanh: một thanh đặc hiện % ở trên, và một `input
 * range` riêng nằm cạnh năm nút preset ở dưới — thanh thứ hai chen vào một
 * hàng đã kín nên nó **thò ra ngoài mép thẻ**. Hai thanh cũng nói cùng một con
 * số bằng hai hình dạng khác nhau, nên người dùng phải tự hiểu rằng chúng liên
 * quan tới nhau.
 *
 * Nay chỉ còn một thanh: nó *là* thanh trượt, và nền của nó *là* phần đã hoàn
 * thành. Hai nút `−` / `+` ở hai đầu cho những lần nhích 5% mà kéo thì khó
 * chính xác — nhất là trên màn hình cảm ứng.
 */
const PRESETS = [0, 25, 50, 75, 100] as const;

/** Bước nhích của `−` / `+` và của thanh trượt. */
const STEP = 5;

interface ProgressQuickActionProps {
  readonly value: number;
  readonly disabled?: boolean;
  /**
   * Checklist của task, nếu có. Có checklist thì `%` là **kết quả** của việc
   * tích từng mục nên không sửa trực tiếp được — chỗ này chỉ hiện.
   */
  readonly checklist?: TaskChecklist | null | undefined;
  /**
   * Task đang ở "cần làm" — hiện % nhưng **không cho sửa**.
   *
   * Một việc chuyển ngược từ "đang làm" về "cần làm" vẫn giữ nguyên % đã làm
   * được, và con số đó là thứ đáng thấy: nó nói việc này đã đi được bao xa
   * trước khi bị đẩy lại. Nhưng sửa nó ở đây thì vô nghĩa — kéo tiến độ mà
   * việc vẫn nằm ngoài luồng đang làm.
   */
  readonly notStarted?: boolean;
  /**
   * Việc ở backlog của dự án Agile — hiện % nhưng **không cho sửa**. Backlog là
   * hàng đợi chưa cam kết; tiến độ chỉ báo được khi việc đã vào sprint. Cửa thật
   * ở `UpdateProgressHandler` (`TASK_IN_BACKLOG`).
   */
  readonly inBacklog?: boolean;
  /**
   * Tích / bỏ tích một mục checklist ngay tại chỗ. Có prop này thì sau dòng
   * "Checklist (0/4)" có nút mở danh sách mục — thanh % vẫn chỉ-đọc, vì cách
   * đổi nó **là** tích từng mục.
   *
   * Chỉ truyền khi người xem tích được (`canToggleChecklist`). Việc chưa bắt
   * đầu hay ở backlog thì nút không hiện dù có prop: server từ chối cùng cửa
   * với thanh trượt (`assertProgressWritable`).
   */
  readonly onToggleChecklist?: ((itemId: string, checked: boolean) => void) | undefined;
  /** Đang gửi một lần tích — khoá các ô để hai cú bấm không chồng nhau. */
  readonly checklistBusy?: boolean;
  readonly onChange: (progressPct: number) => void;
}

export function ProgressQuickAction({
  value,
  disabled = false,
  checklist = null,
  notStarted = false,
  inBacklog = false,
  onToggleChecklist,
  checklistBusy = false,
  onChange,
}: ProgressQuickActionProps): ReactNode {
  const { t } = useTranslation();
  /**
   * Giá trị đang kéo, chưa gửi đi. `null` = không kéo.
   *
   * Cần state riêng vì `value` là dữ liệu server: nếu thanh trượt đọc thẳng
   * `value` thì nó **không nhích** trong lúc kéo (chưa ai gửi gì cả), và người
   * dùng tưởng nó kẹt.
   */
  const [dragging, setDragging] = useState<number | null>(null);
  const shown = dragging ?? value;
  /** Danh sách mục checklist đang mở — mặc định thu, để thẻ giữ gọn như việc thường. */
  const [checklistOpen, setChecklistOpen] = useState(false);

  if (disabled || checklist || notStarted || inBacklog) {
    /*
      Ba lý do khác nhau, cùng một hình dạng: thanh chỉ-đọc kèm **một câu nói
      vì sao**. Thứ tự ưu tiên của câu đó theo mức "làm gì tiếp được":

      - Ở backlog → nói cách mở khoá (đưa vào sprint). Đứng **trước** "chưa
        bắt đầu": chuyển sang Đang làm không gỡ được khoá này.
      - Chưa bắt đầu → nói cách mở khoá (chuyển sang Đang làm). Đây là lý do
        duy nhất người dùng tự gỡ được ngay.
      - Có checklist → nói **đã xong mấy mục trên mấy**, không giải thích cơ
        chế: người đang nhìn thanh % cần biết còn bao nhiêu việc phải tích, còn
        câu "tiến độ tự tính từ checklist" thì đọc một lần là biết.
      - Task cha → % là roll-up từ subtask (spec 9.1), không có gì để bấm.
    */
    // Chỉ việc đang làm, ngoài backlog mới tích được — cùng điều kiện câu ở trên nói ra.
    const toggle =
      checklist && !inBacklog && !notStarted && !disabled ? onToggleChecklist : undefined;
    return (
      <div className="mt-2">
        <Bar value={value} />
        <div className="mt-1 flex items-center gap-1">
          <p className="text-xs text-muted-foreground">
            {inBacklog
              ? t('board.progressInBacklog')
              : notStarted
                ? t('board.progressNotStarted')
                : checklist
                  ? t('checklist.count', {
                      done: checklist.items.filter((item) => item.checked).length,
                      total: checklist.items.length,
                    })
                  : t('board.progressRollup')}
          </p>
          {toggle ? (
            <button
              type="button"
              onClick={() => setChecklistOpen((open) => !open)}
              aria-expanded={checklistOpen}
              aria-label={t(checklistOpen ? 'checklist.collapseItems' : 'checklist.expandItems')}
              className="flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
            >
              <ChevronDown
                aria-hidden
                className={cn('size-3.5 transition-transform', checklistOpen && 'rotate-180')}
              />
            </button>
          ) : null}
        </div>
        {toggle && checklistOpen && checklist ? (
          <ul className="mt-1 flex flex-col gap-1.5">
            {checklist.items.map((item) => (
              <li key={item.id}>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={item.checked}
                    className="ios-check"
                    disabled={checklistBusy}
                    onChange={(event) => toggle(item.id, event.target.checked)}
                  />
                  <span
                    className={cn(
                      'min-w-0 flex-1 whitespace-pre-line break-words',
                      item.checked && 'text-muted-foreground line-through',
                    )}
                  >
                    {item.text}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  const nudge = (delta: number) => {
    const next = Math.min(100, Math.max(0, Math.round((value + delta) / STEP) * STEP));
    if (next !== value) onChange(next);
  };

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      <div className="flex items-center gap-1">
        <NudgeButton
          icon={<Minus />}
          label={t('board.progressDown', { step: STEP })}
          disabled={value === 0}
          onClick={() => nudge(-STEP)}
        />

        <input
          type="range"
          min={0}
          max={100}
          step={STEP}
          value={shown}
          aria-label={t('board.progressSlider')}
          aria-valuetext={`${shown}%`}
          /**
           * `onChange` chỉ cập nhật state cục bộ; chỉ khi **nhả tay** mới gửi
           * đi. Gửi mỗi bước kéo là ~20 request cho một thao tác, và mỗi
           * request là một event trong `event_log`.
           */
          onChange={(event) => setDragging(Number(event.currentTarget.value))}
          onPointerUp={(event) => {
            setDragging(null);
            const next = Number(event.currentTarget.value);
            if (next !== value) onChange(next);
          }}
          onKeyUp={(event) => {
            setDragging(null);
            const next = Number(event.currentTarget.value);
            if (next !== value) onChange(next);
          }}
          onBlur={() => setDragging(null)}
          // Nền là phần đã xong, nên thanh trượt và thanh tiến độ là một.
          style={{
            background: `linear-gradient(to right, var(--color-primary) ${shown}%, var(--color-muted) ${shown}%)`,
          }}
          className={cn(
            'h-2 min-w-0 flex-1 cursor-pointer appearance-none rounded-full',
            '[&::-webkit-slider-thumb]:size-3.5 [&::-webkit-slider-thumb]:appearance-none',
            '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2',
            '[&::-webkit-slider-thumb]:border-background [&::-webkit-slider-thumb]:bg-primary',
            '[&::-moz-range-thumb]:size-3.5 [&::-moz-range-thumb]:rounded-full',
            '[&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-background',
            '[&::-moz-range-thumb]:bg-primary',
          )}
        />

        <NudgeButton
          icon={<Plus />}
          label={t('board.progressUp', { step: STEP })}
          disabled={value === 100}
          onClick={() => nudge(STEP)}
        />

        <span className="w-9 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
          {shown}%
        </span>
      </div>

      {/* Preset vẫn còn: bốn con số này lo phần lớn trường hợp, và một cú bấm
          nhanh hơn một cú kéo có nhắm. */}
      <div className="flex items-center gap-1">
        {PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            onClick={() => onChange(preset)}
            aria-label={t('board.setProgress', { value: preset })}
            aria-pressed={value === preset}
            className={cn(
              'flex-1 rounded-DEFAULT border border-border py-0.5 text-[11px] tabular-nums transition-colors',
              value === preset ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
            )}
          >
            {preset}
          </button>
        ))}
      </div>
    </div>
  );
}

function NudgeButton({
  icon,
  label,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  disabled: boolean;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="flex size-6 shrink-0 items-center justify-center text-muted-foreground disabled:opacity-40 [&_svg]:size-3 rounded-full btn-glass"
    >
      {icon}
    </button>
  );
}

/** Thanh chỉ-đọc, cùng hình dạng với thanh trượt để hai chỗ không lệch nhau. */
function Bar({ value }: { value: number }): ReactNode {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary" style={{ width: `${value}%` }} />
      </div>
      <span className="w-9 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
        {value}%
      </span>
    </div>
  );
}
